import React, { useCallback } from 'react';
import * as Sentry from '@sentry/browser';
import { ZuiErrorPage } from 'components/Zui/ZuiErrorPage';
import AppConfig from 'appConfig';

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  errorInfo: React.ErrorInfo | null;
}

interface ErrorBoundaryProps {
  children: React.ReactNode;
  fallback?: React.ComponentType<{ error: Error; errorInfo: React.ErrorInfo; retry: () => void }>;
  onError?: (error: Error, errorInfo: React.ErrorInfo) => void;
}

/**
 * Global Error Boundary for catching unhandled React component errors
 * Uses ZUI error components for consistent error display
 */
class ErrorBoundaryClass extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    };
  }

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    return {
      hasError: true,
      error,
    };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    const { onError } = this.props;

    // Log error details for debugging
    if (AppConfig.enableShowErrorLog) {
      // eslint-disable-next-line no-console
      console.error('ErrorBoundary caught an error:', error, errorInfo);
    }

    // Update state with error info
    this.setState({ errorInfo });

    // Report to Sentry with proper context
    Sentry.withScope((scope) => {
      scope.setTag('errorBoundary', 'ErrorBoundary');
      scope.setTag('errorCode', this.getErrorCode(error));
      scope.setContext('errorInfo', {
        componentStack: errorInfo.componentStack,
      });
      Sentry.captureException(error);
    });

    // Call custom error handler if provided
    onError?.(error, errorInfo);
  }

  handleRetry = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
    });
  };

  /**
   * Extract error code from various error types
   * Maps to ZUI-supported error codes: '401', '403', '404', '500'
   */
  private getErrorCode(error: Error): '401' | '403' | '404' | '500' {
    // Check for custom error code property
    if ('code' in error && typeof error.code === 'string') {
      const code = error.code;
      if (['401', '403', '404', '500'].includes(code)) {
        return code as '401' | '403' | '404' | '500';
      }
    }

    // Check for HTTP status codes
    if ('status' in error && typeof error.status === 'number') {
      const status = error.status.toString();
      if (['401', '403', '404', '500'].includes(status)) {
        return status as '401' | '403' | '404' | '500';
      }
    }

    // Check for statusCode property (common in Node.js errors)
    if ('statusCode' in error && typeof error.statusCode === 'number') {
      const statusCode = error.statusCode.toString();
      if (['401', '403', '404', '500'].includes(statusCode)) {
        return statusCode as '401' | '403' | '404' | '500';
      }
    }

    // Parse error code from error message patterns
    const errorMessage = error.message || '';

    // Match patterns like "404: Not Found" or "Error 403"
    const codeMatch = errorMessage.match(/(?:^|\s)(401|403|404|500)(?:\s|$|:)/);
    if (codeMatch) {
      return codeMatch[1] as '401' | '403' | '404' | '500';
    }

    // Check for common error types and map to appropriate codes
    const lowerMessage = errorMessage.toLowerCase();

    if (lowerMessage.includes('unauthorized') || lowerMessage.includes('authentication')) {
      return '401'; // Unauthorized
    }

    if (lowerMessage.includes('forbidden') || lowerMessage.includes('permission')) {
      return '403'; // Forbidden
    }

    if (lowerMessage.includes('not found') || lowerMessage.includes('does not exist')) {
      return '404'; // Not Found
    }

    // Default to 500 for all other errors (network, timeout, etc.)
    return '500';
  }

  render() {
    const { hasError, error, errorInfo } = this.state;
    const { children, fallback } = this.props;

    if (!hasError) {
      return children;
    }

    // Use custom fallback component if provided
    if (fallback) {
      const FallbackComponent = fallback;
      return <FallbackComponent error={error!} errorInfo={errorInfo!} retry={this.handleRetry} />;
    }

    // Extract error code from the error
    const errorCode = this.getErrorCode(error!);

    // Default global error page with dynamic error code
    return (
      <ZuiErrorPage
        error={errorCode}
        header="Something went wrong"
        subheader="An unexpected error occurred"
        message="Try refreshing the page or coming back later. If you're still having issues, contact Zywave support."
      />
    );
  }
}

/**
 * Global Error Boundary component
 * Only supports global error boundaries - no feature-level boundaries
 */
export const ErrorBoundary: React.FC<ErrorBoundaryProps> = (props) => {
  return <ErrorBoundaryClass {...props} />;
};

/**
 * Hook for creating error boundary with custom error handling
 */
export const useErrorBoundary = () => {
  const handleError = useCallback((error: Error, errorInfo: React.ErrorInfo) => {
    // Custom error handling logic can be added here
    if (AppConfig.enableShowErrorLog) {
      // eslint-disable-next-line no-console
      console.error('Error caught by useErrorBoundary:', error, errorInfo);
    }
  }, []);

  const createErrorBoundary = useCallback(() => {
    const ErrorBoundaryWrapper = (props: {
      children: React.ReactNode;
      fallback?: React.ComponentType<{
        error: Error;
        errorInfo: React.ErrorInfo;
        retry: () => void;
      }>;
    }) => <ErrorBoundaryClass {...props} onError={handleError} />;

    ErrorBoundaryWrapper.displayName = 'ErrorBoundary';
    return ErrorBoundaryWrapper;
  }, [handleError]);

  return { createErrorBoundary, handleError };
};

/**
 * Higher-order component for wrapping components with error boundary
 * Only supports global error boundaries
 */
export const withErrorBoundary = <P extends object>(
  Component: React.ComponentType<P>,
  fallback?: React.ComponentType<{ error: Error; errorInfo: React.ErrorInfo; retry: () => void }>
) => {
  const WrappedComponent = (props: P) => {
    return (
      <ErrorBoundary fallback={fallback}>
        <Component {...props} />
      </ErrorBoundary>
    );
  };

  WrappedComponent.displayName = `withErrorBoundary(${Component.displayName || Component.name})`;
  return WrappedComponent;
};

/**
 * Custom error fallback components
 */
export const DefaultErrorFallback: React.FC<{
  error: Error;
  errorInfo: React.ErrorInfo;
  retry: () => void;
}> = ({ retry }) => {
  return (
    <ZuiErrorPage
      header="Something went wrong"
      subheader="We apologize for the inconvenience"
      message="Please try again in a few minutes. If the problem continues, contact Zywave support."
      style={{
        minHeight: '300px',
        padding: '20px',
      }}
    >
      <button
        onClick={retry}
        style={{
          marginTop: '16px',
          padding: '8px 16px',
          backgroundColor: '#1976d2',
          color: 'white',
          border: 'none',
          borderRadius: '4px',
          cursor: 'pointer',
        }}
      >
        Try again
      </button>
    </ZuiErrorPage>
  );
};
